'use strict';

import { state, saveAuth, can, isFD, isCoach, homeRoute, loadScheduleLastView } from './state.js';
import { mondayOf } from './date.js';
import { loadInitialData, buildShell, selectSeason } from './shell.js';
import { navigate, redirectTo, mount, el } from './dom.js';
import { setupPushNotifications } from './pwa.js';
import { api } from './api.js';
import { renderLogin, renderRegister, renderPendingApproval, renderForgotPassword, renderResetPassword, navigateAfterSignIn } from './login.js';
import { rememberReturnRoute } from './return-route.js';
import { makeLazy } from './lazy.js';
import { maybeShowOffSeasonModal } from './off-season-modal.js';
import { resolveTrainingRoute } from './training-shell.js';
import { resolvePlanRoute, isShellLessRoute } from './plan-routes.js';
import { beginRoute, routeGuard } from './route-guard.js';
import { allowRoute } from './leave-guard.js';
// Pages with unsaved work register a leave guard (leave-guard.js); re-exported
// here as the router's hook.
export { setLeaveGuard, clearLeaveGuard } from './leave-guard.js';
// Page modules are lazy-loaded on first navigation to their route, so the cold
// download excludes every screen (the biggest, games.js, is ~232KB). Each thunk
// uses a string-literal import() so esbuild can statically split the module into
// its own hashed chunk; the browser caches it after first import.
const onPageLoadError = (name, err) => {
  console.error('failed to load page module', name, err);
  mount(buildShell(el('div', { className: 'error-msg' },
    'Could not load this page. ',
    el('a', { href: '', onclick(e) { e.preventDefault(); location.reload(); } }, 'Reload'))));
};
const lazy = (load, name) => makeLazy(load, name, onPageLoadError, routeGuard);

const renderScheduleView      = lazy(() => import('./schedule.js'), 'renderScheduleView');
const renderDayView           = lazy(() => import('./schedule.js'), 'renderDayView');
const renderPatternsView      = lazy(() => import('./schedule.js'), 'renderPatternsView');
const renderGameDayPracticesView = lazy(() => import('./game-day-practices.js'), 'renderGameDayPracticesView');
const renderCoachHomeView     = lazy(() => import('./coach-home.js'), 'renderCoachHomeView');
const renderCoachTeamsView    = lazy(() => import('./coach-teams.js'), 'renderCoachTeamsView');
const renderPracticePlanView  = lazy(() => import('./practice-plan.js'), 'renderPracticePlanView');
const renderPlansView         = lazy(() => import('./plans.js'), 'renderPlansView');
const renderUserRequestsView  = lazy(() => import('./user-requests.js'), 'renderUserRequestsView');
const renderSeasonsView       = lazy(() => import('./seasons.js'), 'renderSeasonsView');
const renderFieldsView        = lazy(() => import('./fields.js'), 'renderFieldsView');
const renderTeamsView         = lazy(() => import('./teams.js'), 'renderTeamsView');
const renderTeamView          = lazy(() => import('./teams.js'), 'renderTeamView');
const renderBAYSImportView    = lazy(() => import('./teams.js'), 'renderBAYSImportView');
const renderUsersView         = lazy(() => import('./users.js'), 'renderUsersView');
const renderPermissionsView   = lazy(() => import('./permissions.js'), 'renderPermissionsView');
const renderCoachProfileView  = lazy(() => import('./coach-profile.js'), 'renderCoachProfileView');
const renderNotificationsView = lazy(() => import('./notifications.js'), 'renderNotificationsView');
const renderSettingsView      = lazy(() => import('./settings.js'), 'renderSettingsView');
const renderEmailConfirmView  = lazy(() => import('./settings.js'), 'renderEmailConfirmView');
const renderAboutView         = lazy(() => import('./about.js'), 'renderAboutView');
const renderDrillsView        = lazy(() => import('./drills.js'), 'renderDrillsView');
const renderClosingsView      = lazy(() => import('./closings.js'), 'renderClosingsView');
const renderActivityLogView   = lazy(() => import('./activity.js'), 'renderActivityLogView');
const renderRequestsView      = lazy(() => import('./requests.js'), 'renderRequestsView');
const renderGamesView         = lazy(() => import('./games.js'), 'renderGamesView');
const renderBAYSGameSyncView  = lazy(() => import('./games.js'), 'renderBAYSGameSyncView');
const renderGameConflictsView = lazy(() => import('./games.js'), 'renderGameConflictsView');
const renderMakeupRequestsView = lazy(() => import('./games.js'), 'renderMakeupRequestsView');
const renderDivisionView      = lazy(() => import('./division.js'), 'renderDivisionView');
const renderHierarchyView     = lazy(() => import('./hierarchy.js'), 'renderHierarchyView');
const renderFDHomeView        = lazy(() => import('./fd-home.js'), 'renderFDHomeView');
const renderRAHomeView        = lazy(() => import('./ra-home.js'), 'renderRAHomeView');
const renderReservationsView  = lazy(() => import('./reservations.js'), 'renderReservationsView');
const renderAnnouncementsView = lazy(() => import('./announcements-page.js'), 'renderAnnouncementsView');
const renderMaintenanceView   = lazy(() => import('./maintenance.js'), 'renderMaintenanceView');
const renderArchivesView      = lazy(() => import('./archives.js'), 'renderArchivesView');
const renderFieldInfoView     = lazy(() => import('./field-info.js'), 'renderFieldInfoView');
const renderFieldManageView   = lazy(() => import('./field-manage.js'), 'renderFieldManageView');
const renderScheduleMapView   = lazy(() => import('./schedule-map.js'), 'renderScheduleMapView');
const renderFieldMapsView     = lazy(() => import('./field-maps.js'), 'renderFieldMapsView');
const renderFieldUsageView    = lazy(() => import('./field-usage.js'), 'renderFieldUsageView');
const renderTrainingOverviewView = lazy(() => import('./training-overview.js'), 'renderTrainingOverviewView');
const renderTrainingView      = lazy(() => import('./training.js'), 'renderTrainingView');
const renderTrainingTrainersView = lazy(() => import('./training-trainers.js'), 'renderTrainingTrainersView');
const renderTrainingConfigView = lazy(() => import('./training-config.js'), 'renderTrainingConfigView');
const renderTrainerBoard      = lazy(() => import('./trainer-board.js'), 'renderTrainerBoard');
const renderPlanShareView     = lazy(() => import('./plan-share.js'), 'renderPlanShareView');

export async function router() {
  // A page with unsaved work may decline the hash change; the previous hash
  // is then put back and nothing routes, so the page stays as it was.
  if (!(await allowRoute(location.hash))) return;
  // Every navigation starts a new route; renders still running for an earlier
  // one see their guard go stale and stop before mounting (route-guard.js).
  const isCurrent = beginRoute();
  try {
    await routeInner(isCurrent);
  } finally {
    // Runs after the view has mounted. closeModal() removes every
    // .modal-overlay on the page, so a modal opened mid-render can be destroyed
    // by the render that follows it. `finally` (rather than a statement after
    // the await) also means a view that throws still gets the explanation; the
    // error itself keeps propagating, since nothing catches it here. A route
    // that was superseded leaves it to the newer one.
    // Public pages (/plan/, /trainer/) have no shell, so no modal over them.
    if (isCurrent() && !isShellLessRoute(location.hash)) maybeShowOffSeasonModal();
  }
}

async function routeInner(isCurrent) {
  const hash = location.hash.replace(/^#/, '') || '/schedule';
  // The route without its query. Exact routes match on this, so a deep link
  // such as #/games?focus=<id> (every notification link) reaches its view
  // instead of falling through to the schedule at the bottom.
  const path = hash.split('?')[0];

  // Handle password reset link BEFORE the auth guard — the user is not logged in.
  if (hash.startsWith('/reset-password')) {
    const params = new URLSearchParams(hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '');
    await renderResetPassword(params.get('token'));
    return;
  }

  // Handle forgot-password route BEFORE the auth guard.
  if (hash === '/forgot-password') {
    renderForgotPassword();
    return;
  }

  // Handle email confirmation link BEFORE the auth guard — the user may not be logged in.
  if (hash.startsWith('/confirm-email')) {
    const params = new URLSearchParams(hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '');
    await renderEmailConfirmView(params.get('token'));
    return;
  }

  // Handle OAuth callback BEFORE the auth guard — the user has no token yet.
  if (hash.startsWith('/auth/callback')) {
    const params = new URLSearchParams(hash.slice('/auth/callback'.length));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    if (accessToken && refreshToken) {
      saveAuth(accessToken, refreshToken);
      await loadInitialData();
      navigateAfterSignIn();
      setupPushNotifications();
      if (window.__reconnectSSE) window.__reconnectSSE();
      return;
    }
    if (params.get('status') === 'pending_approval') {
      renderPendingApproval();
      return;
    }
    await renderLogin();
    return;
  }

  // Trainer board — no account. The token stays in the fragment (never a query
  // string) and is sent as a header by trainer-board.js.
  if (hash.startsWith('/trainer/')) {
    await renderTrainerBoard(hash.slice('/trainer/'.length).split('?')[0]);
    return;
  }

  // Shared practice plan — no account, signed in or not. The token is sent in
  // the API path by plan-share.js, never with a JWT. '/plans/…' (the coach's
  // pages) does not start with '/plan/', and resolvePlanRoute never claims this.
  if (hash.startsWith('/plan/')) {
    await renderPlanShareView(hash.slice('/plan/'.length).split('?')[0]);
    return;
  }

  if (!state.token) {
    // Keep the deep link (an email's #/...?focus=) for after sign-in; the
    // helper skips login/register/auth routes and an empty hash.
    rememberReturnRoute(location.hash);
    if (hash === '/register') { renderRegister(); return; }
    await renderLogin();
    return;
  }

  if (path === '/login' || path === '/register') {
    if (!state.initialized) await loadInitialData();
    if (!isCurrent()) return;
    navigate(homeRoute());
    return;
  }

  if (!state.initialized) await loadInitialData();
  // A newer route started while the initial data loaded; it renders instead.
  if (!isCurrent()) return;

  // Management pages don't require a current season.
  if (path === '/seasons')       { await renderSeasonsView();       return; }
  if (path === '/fields')        { await renderFieldsView();        return; }
  // Field Maps sits above the /fields/ handlers below; the paths don't overlap
  // (no trailing slash), but keeping them together makes that obvious.
  if (path === '/field-maps' || hash.startsWith('/field-maps?')) {
    const params = new URLSearchParams(hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '');
    await renderFieldMapsView(params.get('tab'));
    return;
  }
  if (path === '/teams')              { await renderTeamsView();         return; }
  if (path === '/teams/bays-import')  { await renderBAYSImportView();    return; }
  if (hash.startsWith('/teams/')) {
    const teamId = hash.slice('/teams/'.length).split('?')[0];
    await renderTeamView(teamId);
    return;
  }
  if (path === '/users')         { await renderUsersView();         return; }
  if (path === '/permissions')   { await renderPermissionsView();   return; } // FD only — the view redirects everyone else home
  if (hash.startsWith('/coaches/')) {
    const coachId = hash.slice('/coaches/'.length).split('?')[0];
    await renderCoachProfileView(coachId);
    return;
  }
  if (hash.startsWith('/fields/') && hash.includes('/manage')) {
    const fieldId = hash.slice('/fields/'.length).replace('/manage', '').split('?')[0];
    await renderFieldManageView(fieldId);
    return;
  }
  if (hash.startsWith('/fields/')) {
    const fieldId = hash.slice('/fields/'.length).split('?')[0];
    await renderFieldInfoView(fieldId);
    return;
  }
  if (path === '/notifications' || hash.startsWith('/notifications?')) {
    const params = new URLSearchParams(hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '');
    await renderNotificationsView(params.get('focus'));
    return;
  }
  if (path === '/archives')      { await renderArchivesView();      return; }
  if (path === '/settings' || hash.startsWith('/settings#')) {
    const section = hash.startsWith('/settings#') ? hash.slice('/settings#'.length) : undefined;
    await renderSettingsView(section);
    return;
  }
  if (path === '/about')         { await renderAboutView();        return; }
  // The drill library is season-independent, so it sits above the season gate.
  if (path === '/drills' || hash.startsWith('/drills?') || hash.startsWith('/drills/')) {
    const q = hash.includes('?') ? new URLSearchParams(hash.slice(hash.indexOf('?') + 1)) : null;
    const id = hash.startsWith('/drills/') ? hash.slice('/drills/'.length).split('?')[0] : null;
    await renderDrillsView({ id, age: q?.get('age') || null });
    return;
  }

  if (!state.currentSeasonId) {
    mount(buildShell(el('div', { style: 'text-align:center;padding:60px;' },
      el('p', { style: 'color:var(--muted);margin-bottom:16px;font-size:15px;' }, 'No seasons yet.'),
      isFD() ? el('button', { className: 'btn btn-primary',
        onclick() { navigate('/seasons'); }
      }, '+ Create Season') : null,
    )));
    return;
  }

  if (path === '/fd-home')       { await renderFDHomeView();         return; }
  if (path === '/field-usage')   { await renderFieldUsageView();     return; }
  if (path === '/ra-home')       { await renderRAHomeView();         return; }
  if (path === '/division')      { await renderDivisionView();       return; }
  if (path === '/hierarchy')     { await renderHierarchyView();     return; }
  const training = resolveTrainingRoute(hash);
  if (training) {
    if (training.redirect) { redirectTo(training.redirect); return; }
    switch (training.section) {
      case 'overview': await renderTrainingOverviewView(); return;
      case 'queue':    await renderTrainingView({ tab: training.tab, focus: training.focus }); return;
      case 'trainers': await renderTrainingTrainersView(); return;
      case 'config':   await renderTrainingConfigView(); return;
    }
  }
  // Practice plans: the hub tabs, a plan by id, and a practice's plan.
  const planRoute = resolvePlanRoute(path);
  if (planRoute) {
    if (planRoute.kind === 'hub') await renderPlansView({ tab: planRoute.tab });
    else if (planRoute.kind === 'plan') await renderPracticePlanView({ planId: planRoute.planId });
    else await renderPracticePlanView({ occurrenceId: planRoute.occurrenceId });
    return;
  }
  if (path === '/coach-home' || hash.startsWith('/coach-home?')) {
    const chParams = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : '');
    await renderCoachHomeView({ tab: chParams.get('tab') });
    return;
  }
  if (path === '/game-day-practices') { await renderGameDayPracticesView(); return; }
  if (path === '/coach-teams') {
    await renderCoachTeamsView();
    return;
  }
  if (path === '/my-schedule')  { navigate('/coach-home');          return; }
  if (path === '/games/bays-sync') { await renderBAYSGameSyncView(); return; }
  if (path === '/conflicts' || path === '/game-conflicts') { await renderGameConflictsView(); return; }
  if (path === '/game-actions')                            { await renderGameConflictsView(); return; } // legacy redirect
  if (path === '/game-requests')   { await renderMakeupRequestsView();  return; }
  if (path === '/makeup-requests') { await renderMakeupRequestsView();  return; } // legacy redirect
  if (path === '/games')        { await renderGamesView();          return; }
  if (path === '/requests')     { await renderRequestsView();       return; }
  if (path === '/user-requests' || hash.startsWith('/user-requests?')) {
    const params = new URLSearchParams(hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '');
    await renderUserRequestsView(params.get('tab'));
    return;
  }
  if (path === '/coach-requests') { await renderUserRequestsView();  return; } // legacy redirect
  if (path === '/role-requests')  { await renderUserRequestsView();  return; } // legacy redirect
  if (path === '/closings')      { await renderClosingsView();       return; }
  if (path === '/announcements') {
    if (!can('send_announcements')) { redirectTo(homeRoute()); return; }
    await renderAnnouncementsView();
    return;
  }
  if (path === '/maintenance' || hash.startsWith('/maintenance?')) {
    const mParams = new URLSearchParams(hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '');
    await renderMaintenanceView(mParams.get('tab'));
    return;
  }
  if (path === '/reservations' || hash.startsWith('/reservations?')) {
    const params = new URLSearchParams(hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '');
    await renderReservationsView(params.get('focus'));
    return;
  }
  if (path === '/activity-log') { await renderActivityLogView();    return; }

  if (hash === '/schedule/patterns') {
    await renderPatternsView();
  } else if (hash.startsWith('/schedule/map')) {
    const qs = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
    const params = new URLSearchParams(qs);
    await renderScheduleMapView(params.get('date'), params.get('from'));
  } else if (hash.startsWith('/schedule/day')) {
    const qs = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
    const params = new URLSearchParams(qs);
    const date = params.get('date');
    const fieldId = params.get('field');
    const from = params.get('from'); // 'schedule' | 'day' | 'division' | 'coach-home' | 'coach-profile'
    const backPath = params.get('back'); // e.g. '/coaches/abc-123' for from=coach-profile
    await renderDayView(date, fieldId, from, backPath);
  } else if (hash === '/schedule') {
    // Restore the last-viewed date (but always render as grid).
    const last = loadScheduleLastView();
    if (last?.date) {
      state.currentWeek = last.view === 'grid' ? last.date : mondayOf(last.date);
    }
    await renderScheduleView();
  } else if (hash.startsWith('/schedule')) {
    await renderScheduleView();
  } else {
    await renderScheduleView();
  }
}
